import React, { useState, useEffect } from 'react';
import { clothingService } from '../services/clothingService';
import { FaPlus, FaTimes, FaTshirt, FaWater, FaChevronDown, FaChevronUp, FaTrash, FaEdit } from 'react-icons/fa';

function Clothing() {
  const [showForm, setShowForm] = useState(false);
  const [clothingItems, setClothingItems] = useState([]);
  const [newItem, setNewItem] = useState({
    name: 'Black T-Shirt',
    type: 'Shirt',
    brand: 'H&M',
    purchaseDate: new Date().toISOString().split('T')[0],
    price: '29.99',
    lastWashDate: new Date().toISOString().split('T')[0],
    wearsAfterWash: 0
  });
  const [expandedItem, setExpandedItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    loadClothingItems();
  }, []);

  const loadClothingItems = async () => {
    try {
      const response = await clothingService.getItems();
      setClothingItems(response.data);
    } catch (error) {
      console.error('Error loading clothing items:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await clothingService.updateItem(editingItem._id, newItem);
        setEditingItem(null);
      } else {
        await clothingService.createItem(newItem);
      }
      loadClothingItems();
      setNewItem({ name: '', type: '', brand: '', purchaseDate: '', price: '', lastWashDate: '', wearsAfterWash: 0 });
      setShowForm(false);
    } catch (error) {
      console.error('Error saving item:', error);
    }
  };

  const handleWearCount = async (itemId) => {
    try {
      await clothingService.recordWear(itemId);
      loadClothingItems();
    } catch (error) {
      console.error('Error recording wear:', error);
    }
  };

  const handleWashed = async (itemId) => {
    try {
      await clothingService.recordWash(itemId);
      loadClothingItems();
    } catch (error) {
      console.error('Error recording wash:', error);
    }
  };

  const handleDelete = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await clothingService.deleteItem(itemId);
        loadClothingItems();
      } catch (error) {
        console.error('Error deleting item:', error);
      }
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setNewItem(item);
    setShowForm(true);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Clothing Management</h1>
        <button 
          onClick={() => {
            setShowForm(!showForm);
            setEditingItem(null);
            setNewItem({ name: '', type: '', brand: '', purchaseDate: '', price: '', lastWashDate: '', wearsAfterWash: 0 });
          }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-sm"
        >
          {showForm ? <FaTimes className="text-sm" /> : <FaPlus className="text-sm" />}
          <span>{showForm ? 'Cancel' : 'Add New Item'}</span>
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 mb-8 space-y-4">
          <h2 className="text-xl font-semibold mb-4">{editingItem ? 'Edit Item' : 'Add New Item'}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <input
              type="text"
              placeholder="Name"
              value={newItem.name}
              onChange={(e) => setNewItem({...newItem, name: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
            <input
              type="text"
              placeholder="Type"
              value={newItem.type}
              onChange={(e) => setNewItem({...newItem, type: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
            <input
              type="text"
              placeholder="Brand"
              value={newItem.brand}
              onChange={(e) => setNewItem({...newItem, brand: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
            <input
              type="date"
              value={newItem.purchaseDate}
              onChange={(e) => setNewItem({...newItem, purchaseDate: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
            <input
              type="number"
              placeholder="Price"
              value={newItem.price}
              onChange={(e) => setNewItem({...newItem, price: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
              step="0.01"
            />
            <input
              type="date"
              placeholder="Last Wash Date"
              value={newItem.lastWashDate}
              onChange={(e) => setNewItem({...newItem, lastWashDate: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          <button 
            type="submit" 
            className="w-full md:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200"
          >
            {editingItem ? 'Update Item' : 'Add Item'}
          </button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {clothingItems.map(item => (
          <div key={item._id} 
               className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow duration-200">
            <div className="flex justify-between items-start mb-2">
              <div className="flex-1">
                <h3 className="font-semibold text-lg text-gray-800">{item.name}</h3>
                <p className="text-gray-600 text-sm">{item.brand}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                  {item.wearsAfterWash} uses
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between mt-4">
              <button
                onClick={() => setExpandedItem(expandedItem === item._id ? null : item._id)}
                className="text-gray-500 hover:text-gray-700"
              >
                {expandedItem === item._id ? <FaChevronUp /> : <FaChevronDown />}
              </button>
              
              <div className="flex gap-2">
                <button
                  onClick={() => handleWearCount(item._id)}
                  className="p-2 text-purple-600 hover:bg-purple-50 rounded-full"
                  title="Mark as worn"
                >
                  <FaTshirt className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleWashed(item._id)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-full"
                  title="Mark as washed"
                >
                  <FaWater className="w-4 h-4" />
                </button>
              </div>
            </div>

            {expandedItem === item._id && (
              <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-600">
                <div className="space-y-2 mb-4">
                  <p>Type: {item.type}</p>
                  <p>Purchase Date: {new Date(item.purchaseDate).toLocaleDateString()}</p>
                  <p>Price: ${Number(item.price).toFixed(2)}</p>
                  <p>Last Wash: {new Date(item.lastWashDate).toLocaleDateString()}</p>
                </div>
                <div className="flex justify-end gap-2 mt-4 border-t pt-4">
                  <button
                    onClick={() => handleEdit(item)}
                    className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-full transition-colors"
                    title="Edit item"
                  >
                    <FaEdit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(item._id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                    title="Delete item"
                  >
                    <FaTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Clothing;
